import { dao } from '@platform/dao';
import { buildFilter } from '@platform/core/lib/filterDsl';
import type { IRecordListParams, IRecordStats } from '@platform/core/types';
import { baseApi, run, statsTag } from '@platform/state/api';
import { BA_COVERAGE } from './baCoverageSpec';
import type { IBaCoverageArg } from './IBaCoverageArg';
import type { IBaCoverageStats } from './IBaCoverageStats';

const countsOf = (s: IRecordStats): Record<string, number> =>
  Object.fromEntries(s.buckets.filter((b) => b.value !== null).map((b) => [String(b.value), b.count]));
const eq = (field: string, value: string): string => `${field}:eq:${encodeURIComponent(value)}`;
const DONE = `status:in:${BA_COVERAGE.doneStatuses.map(encodeURIComponent).join(',')}`;

/** Усі запити статистики покриття паралельно: за видом (і загальна кількість), за модулем для кожного виду, виконані вимоги, сторони. */
async function load({ ws, rt, params, kinds }: IBaCoverageArg): Promise<IBaCoverageStats> {
  const { q, filter, preset } = params;
  const base: IRecordListParams = { q, filter, preset };
  const narrow = (extra: string): IRecordListParams => ({ ...base, filter: buildFilter([filter, extra]) });
  const by = async (field: string, kind: string) => [kind, countsOf(await dao.records.stats(ws, rt, field, narrow(eq('kind', kind))))] as const;
  const [all, perKind, done, stakes] = await Promise.all([
    dao.records.stats(ws, rt, 'kind', base),
    Promise.all(kinds.map((k) => by('module', k))),
    dao.records.stats(ws, rt, 'module', narrow(`${eq('kind', BA_COVERAGE.requirement)};${DONE}`)),
    Promise.all(BA_COVERAGE.stakeKinds.map((k) => by('stakeholder', k))),
  ]);
  return { total: all.total, byKind: countsOf(all), byKindModule: Object.fromEntries(perKind), doneByModule: countsOf(done), byKindStake: Object.fromEntries(stakes) };
}

/**
 * Статистика покриття аналізом (прототип v4 isBaDash) одним запитом RTK: кеш — за простором, типом, фільтрами сторінки й видами; зміни
 * записів типу (тег статистики) її перечитують.
 */
export const baCoverageApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    baCoverageStats: b.query<IBaCoverageStats, IBaCoverageArg>({
      queryFn: (arg) => run(() => load(arg)),
      providesTags: (_r, _e, { rt }) => [statsTag(rt)],
    }),
  }),
});

export const { useBaCoverageStatsQuery } = baCoverageApi;
